(ns frontend.view.grid.investors-grid.events
  (:require
   [common.attribute :as attribute]
   [common.localization :as loc]
   [common.schema.entity-types :as entity-types]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.named-view :as named-view-actions]
   [frontend.conn :as conn]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.grid.companies-grid.events :as companies-events]
   [frontend.view.grid.investors-grid.columns :as columns]
   [frontend.view.grid.investors-grid.db :as db]
   [frontend.view.grid.persons-grid.events :as persons-events]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.error-message.view :as error-message]
   [frontend.view.modals.events :as modals]
   [promesa.core :as p]
   [reagent.core :as r]))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :investors-view-settings]
   rest))

(defn current-legal-entity-types
  []
  @session/investor-legal-entity-types
  #_(->> @session/all-investors
         (map :investor/legal-entity)
         (keep (fn [id]
                 (->> id
                      (get @session/entities)
                      :entity/type)))
         set))

(defn get-label
  [k]
  (let [legal-entity-types (current-legal-entity-types)
        tenant-schema @session/tenant-schema
        ref-field? (vector? k)
        [_ investor-field] (when (and (vector? k)
                                      (= (first k)
                                         :investor/legal-entity))
                             k)]
    (cond
      (nil? k)
      nil

      (keyword k)
      (let [fs (registry/get-field-schema tenant-schema k)]
        (loc/field-name k fs))

      ;; listing explicit clauses in case we need more fine-tuned
      ;; control for some of these besides attribute/attribute-label:
      (and ref-field?
           (= (count legal-entity-types) 1))
      (attribute/attribute-label tenant-schema k)

      (and ref-field?
           (> (count legal-entity-types) 1)
           (= (namespace investor-field) "common"))
      (attribute/attribute-label tenant-schema k)

      (and ref-field?
           (> (count legal-entity-types) 1)
           (= (namespace investor-field) "person"))
      (loc/tr :legal-entity-ref-field
          [(loc/tr :person-lc)
           (let [fs (registry/get-field-schema tenant-schema investor-field)]
             (loc/field-name investor-field fs))])

      (and ref-field?
           (> (count legal-entity-types) 1)
           (contains? #{"company" "asset"} (namespace investor-field)))
      (loc/tr :legal-entity-ref-field
          [(loc/tr :company-lc)
           (when-let [fs (registry/get-field-schema tenant-schema investor-field)]
             (loc/field-name investor-field fs))])

      :else
      (attribute/attribute-label tenant-schema k))))

(defn precalculated-schema
  [recalculate?]
  (let [calculated @db/schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/investor)
           (remove (fn [[k v]]
                     (let [grid-visibility (->> v
                                                :field/default-visibility
                                                :grid)]
                       (or (nil? grid-visibility)
                           (= grid-visibility :editor.type/none)
                           (if (or (not= (:field/data-type v) :data.type/currency-symbol)
                                   (not (:field/secondary-field? v)))
                             (not (f-permissions/field-read-access? k :entity.type/investor nil))
                             (not (f-permissions/field-read-access? (:field/related-fields v)
                                                                    :entity.type/investor
                                                                    nil)))))))
           (sort-by (fn [[_k v]]
                      (-> v
                          :field/translations
                          :en)))))
        legal-entity-types (current-legal-entity-types)
        company-schema (when (and (or recalculate?
                                      (not calculated))
                                  (contains? legal-entity-types :entity.type/company))
                         (companies-events/precalculated-schema recalculate?))
        person-schema (when (and (or recalculate?
                                     (not calculated))
                                 (contains? legal-entity-types :entity.type/person))
                        (persons-events/precalculated-schema recalculate?))
        legal-entity-schema (when (or recalculate?
                                      (not calculated))
                              (->> (concat company-schema person-schema)
                                   (map (fn [[k v :as _u]]
                                          [[:investor/legal-entity k] v]))
                                   (into {})
                                   (sort-by (fn [[_ v]]
                                              (-> v
                                                  :field/translations
                                                  :en)))
                                   doall))
        schema (if (and (not recalculate?)
                        calculated)
                 schema
                 (concat schema legal-entity-schema))]

    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn investors-column-keys
  []
  (concat
   (when (session/is-admin?) [:db/id])
   (map first (keys-and-schemas))))

(def default-kanban-attributes [:investor/description :investor/interest-in-products])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes
        (set
         (concat @db/selected-columns
                 attributes
                 default-kanban-attributes
                 [footer-1 footer-2 (entity-types/type->owner-attribute :entity.type/investor)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k _fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

(defn column-tabs
  []
  (let [legal-entity-types (current-legal-entity-types)]
    (concat [{:key   :entity.type/investor
              :label (loc/tr :entity.type/investor)}]
            (when (= (count legal-entity-types) 1)
              [{:key   (first legal-entity-types)
                :label (loc/tr :legal-entity)}])
            (when (> (count legal-entity-types) 1)
              (concat
               [{:key   :entity.type/legal-entity
                 :label (loc/tr :legal-entity)}]
               (->> legal-entity-types
                    (map (fn [et]
                           {:key   et
                            :label (loc/tr :legal-entity-specified [(loc/tr et)])}))))))))

(defn resolve-tab
  [field]
  (let [ref-field? (and (vector? field)
                        (keyword? (first field))
                        (keyword? (second field)))
        [_ref attr] (when ref-field?
                      field)
        legal-entity-types (when ref-field?
                             (current-legal-entity-types))]
    (cond
      (keyword? field)
      :entity.type/investor
      (and ref-field?
           (= (count legal-entity-types) 1))
      (first legal-entity-types)
      (and ref-field?
           (> (count legal-entity-types) 1)
           (= (namespace attr) "common"))
      :entity.type/legal-entity
      (and ref-field?
           (> (count legal-entity-types) 1)
           (or (= (namespace attr) "company")
               (= (namespace attr) "asset")))
      :entity.type/company
      (and ref-field?
           (> (count legal-entity-types) 1)
           (= (namespace attr) "person"))
      :entity.type/person
      :else
      :unknown)))

(def default-grouped-by
  :investor/typical-investment-range)

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state db/investors
   :entity-type :entity.type/investor
   :modal-state db/modal-state
   :selected-columns-state db/selected-columns
   :ordered-columns-state db/ordered-columns
   :filters-state db/investor-filters
   :view-settings-state db/view-settings
   :column-widths-state db/column-widths
   :selected-pipeline nil
   :ls-path ls-path
   :kanban? true
   :kanban-args {:default-grouped-by default-grouped-by
                 :kanban-state        db/kanban
                 :grouped-by-state    db/grouped-by
                 :card-settings-state db/card-settings
                 ;;for long card view:
                 :default-attributes  default-kanban-attributes
                 :expanded-state      db/expanded
                 :sorted-kanban-entities-state db/sorted-kanban-entities
                 :lists-state         db/lists
                 :expanded?           expanded?
                 :toggle-expand!      toggle-expand!}
   :column-keys investors-column-keys
   :column-tabs (column-tabs)
   :resolve-tab resolve-tab
   :default-columns columns/default-columns
   :mandatory? columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn expanded?
  [entity-id]
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  [args]
  (grid/resolve-column-key-universe args))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/investors)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/investors)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/investors)]
    (r/dispose! tracker))

  (swap! db/investors dissoc
    :loaded
    :schema
    :entities
    :entity-types
    :view-settings
    :sorted-grid-entities
    :search
    :kanban
    :sorted-kanban-entities
    :selected
    :grid-entities))

(defn toggle-quick-filters!
  []
  (swap! db/investors update :show-quick-filters? not))

(defn system-views
  []
  [{:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :investor/name
                          :sort-direction   "ASC"
                          :view-mode        :grid-mode}
    :named-view/name (loc/tr :all-investors)
    :disable-delete? true
    :db/id "named-view-all-investors"}
   {:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :investor/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode}
    :named-view/name (loc/tr :investor-pipeline)
    :disable-delete? true
    :db/id "named-view-investor-pipeline"}])

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :investors)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views)
                                     first
                                     :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn update-last-view->ls
  [named-view]
  (ls/set-in (ls-path) (:named-view/settings named-view)))

(defn load-view!
  [named-view] ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (update-last-view->ls named-view)
    (setup-column-state! settings)))


(defn investors-updates
  []
  (swap! db/investors assoc :grid-entities @session/all-investors))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/investors assoc :loaded true)
                    (deref db/investors))))))

(defn setup
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/investors assoc
      :tracker-1 (r/track! investors-updates)
      :tracker-2 (r/track! permission-updates)
      :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :investors
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
     msg
     identity
     (fn [& a] ;;TODO show error message
       (logging/error "create-named-view failed" a)))))

(defn mass-delete!
  []
  (-> (conn/sendp! :investor/mass-action
                   {:action       :delete-investors
                    :investor-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [_args]
                (swap! db/investors assoc :selected {})))
      (p/catch (fn [e]
                 (logging/error "Unable to delete selected investors" e)))))

(defn delete-selected-clicked
  [_]
  (let [investor-ids (grid/get-selected-items (setup-args))
        target-in (map session/target-investor-in investor-ids)
        cust-in (map session/customer-in investor-ids)]
    (cond
      (not-every? empty? cust-in)
      (modals/open-modal! error-message/modal-key
                          {:body-content   (loc/tr :customer-cannot-be-deleted)
                           :title          (loc/tr :attention)
                           :ok-button-text (loc/tr :back)})

      (not-every? empty? target-in)
      (modals/open-modal!
       error-message/modal-key
       {:body-content   (loc/tr :investor-cannot-be-deleted)
        :title          (loc/tr :attention)
        :ok-button-text (loc/tr :back)})

      :else
      (modals/open-modal!
       confirmation/modal-key
       {:body          [:div
                        [:p (loc/tr :confirm-delete-selected)]]
        :confirm-text  (loc/tr :confirm-delete-selected-button)
        :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
        :on-confirm    #(mass-delete!)}))))

(defn expand-all!
  []
  (swap! db/kanban assoc :card-mode :long-card)
  (reset! db/expanded {}))

(defn collapse-all!
  []
  (swap! db/kanban assoc :card-mode :short-card)
  (reset! db/expanded {}))

(defn open-card-settings!
  []
  (swap! db/kanban assoc :card-settings? true))

(defn close-card-settings!
  []
  (swap! db/kanban dissoc :card-settings?))
